import React, { useEffect, useState } from "react"
import i18n from "../services/i18n"

// ============================================================================
// tog'a #2389 (2026-09-23): «Nega hali ham so'mda kiritganlar alohida ko'rinmayapti?»
//
// JONLI DALIL: tuzatish KECHA 20:08 dan beri serverda edi (har build'da bor — tekshirildi),
// lekin tog'aning iPad'idagi tab kechqurundan beri QAYTA YUKLANMAGAN edi. Kadrdagi ustunlar
// («Приход | Расход», 150px) aynan eski versiyaniki (`bd10e87`), yangisida «Приход $ | Приход сум».
//
// Nega bunday bo'ladi: bu SPA — sahifalar orasida yurish index.html ni qayta so'ramaydi, xotiradagi
// eski JS ishlayveradi. Server keshi to'g'ri (index.html `no-cache`), lekin tab ochiq tursa (planshetda
// kunlab) yangi versiya umuman olinmaydi. `ErrorBoundary` faqat eski tab yangi chunk'ni topa olmay
// YIQILGANDA qayta yuklaydi — sahifa yiqilmasa foydalanuvchi eski versiyada qolib ketadi.
//
// Yechim: vaqti-vaqti bilan (va tabga qaytilganda) serverdagi index.html ochiq sahifa bilan solishtiriladi
// (tashqi skriptlar + inline webpack runtime — pastda nega ikkalasi kerakligi yozilgan). Farq bo'lsa —
// «yangi versiya chiqdi» degan ogohlantirish chiqadi.
// ⚠️ Sahifa O'ZI qayta yuklanmaydi: ochiq formada kiritilgan ma'lumot yo'qolmasin — foydalanuvchi bosadi.
// ============================================================================

const TEKSHIRUV_ORALIGI = 5 * 60 * 1000   // 5 daqiqa (index.html ~6 KB — yuk sezilmaydi)
const MAIN_RE = /\/static\/js\/main\.[a-z0-9]+\.chunk\.js/

// ⛔ JONLI TOPILDI (2026-09-23, birinchi versiyadan keyin): faqat `main.*.chunk.js` nomini solishtirish
// YETARLI EMAS. Deploy faqat bitta sahifani o'zgartirsa (masalan hamyon — alohida lazy chunk), `main`
// nomi O'ZGARMAYDI: 20cbc29 va 3fbc8a0 build'larida index.html dagi skriptlar AYNAN bir xil edi.
// O'zgargani — index.html ICHIDAGI inline webpack runtime (lazy chunk'lar xaritasi). Shuning uchun
// versiya belgisi ikki qismdan iborat:
//   1) index.html dagi har bir TASHQI skript hozirgi sahifada yuklangan bo'lishi kerak;
//   2) inline runtime matni (tarkibida «.chunk.js» bor skript) hozirgisi bilan bir xil bo'lishi kerak.
const RUNTIME_BELGI = ".chunk.js"

/** Sahifadagi inline webpack runtime matni (dev rejimda yo'q bo'lishi mumkin). */
const joriyRuntime = (): string | null => {
  for (const s of Array.from(document.scripts)) {
    if (!s.src && (s.textContent || "").includes(RUNTIME_BELGI)) return (s.textContent || "").trim()
  }
  return null
}

/** Tekshiruvni yoqish mumkinmi: production build'da `main.*.chunk.js` yuklangan bo'ladi. */
const productionmi = () => Array.from(document.scripts).some((s) => MAIN_RE.test(s.src || ""))

/** Serverdagi index.html hozir ochiq sahifadan BOSHQA versiyami. */
export const boshqaVersiyami = (html: string, yuklanganSrc: string[], runtime: string | null): boolean => {
  // 1) tashqi skriptlar: yangi index.html dagi har bir fayl hozir yuklangan bo'lishi kerak
  const tashqi = (html.match(/\/static\/js\/[^"'\s>]+\.js/g) || [])
  if (tashqi.some((f) => !yuklanganSrc.some((src) => src.endsWith(f)))) return true
  // 2) inline runtime: lazy sahifalar xaritasi shu yerda
  if (runtime) {
    const inline = Array.from(html.matchAll(/<script>([\s\S]*?)<\/script>/g)).map((m) => m[1].trim())
      .find((t) => t.includes(RUNTIME_BELGI))
    if (inline !== undefined && inline !== runtime) return true
  }
  return false
}

export default function NewVersionBanner() {
  const [yangi, setYangi] = useState(false)

  useEffect(() => {
    if (!productionmi()) return   // `npm start` (dev) da tekshiruv o'chiq
    const runtime = joriyRuntime()
    let toxtadi = false

    const tekshir = async () => {
      if (toxtadi || document.visibilityState === "hidden") return
      try {
        const r = await fetch("/index.html?v=" + Date.now(), { cache: "no-store" })
        if (!r.ok) return
        const html = await r.text()
        const yuklangan = Array.from(document.scripts).map((s) => s.src || "").filter(Boolean)
        if (!toxtadi && boshqaVersiyami(html, yuklangan, runtime)) setYangi(true)
      } catch {
        // tarmoq xatosi — keyingi tekshiruvda qayta urinadi
      }
    }

    const tabgaQaytdi = () => { if (document.visibilityState === "visible") tekshir() }
    const t = window.setInterval(tekshir, TEKSHIRUV_ORALIGI)
    document.addEventListener("visibilitychange", tabgaQaytdi)
    window.addEventListener("focus", tekshir)
    return () => {
      toxtadi = true
      window.clearInterval(t)
      document.removeEventListener("visibilitychange", tabgaQaytdi)
      window.removeEventListener("focus", tekshir)
    }
  }, [])

  if (!yangi) return null
  return (
    <div
      role="alert"
      data-testid="new-version-banner"
      style={{
        position: "fixed", left: "50%", bottom: 20, transform: "translateX(-50%)", zIndex: 1100,
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "center",
        maxWidth: "calc(100vw - 32px)", padding: "10px 14px", borderRadius: 12,
        background: "#7d5f1d", color: "#fff", boxShadow: "0 8px 24px rgba(0,0,0,.25)", fontSize: 14,
      }}
    >
      <span>{i18n.t("newVersionAvailable", "Вышла новая версия системы — обновите страницу")}</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          minHeight: 36, padding: "6px 16px", borderRadius: 8, border: "none", cursor: "pointer",
          background: "#fff", color: "#7d5f1d", fontWeight: 700, fontSize: 14,
        }}
      >
        {i18n.t("refresh", "Обновить")}
      </button>
    </div>
  )
}
